import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AppTabs } from '../shared/tabs/AppTabs';
import { AppTabPanel } from '../shared/tabs/AppTabPanel';
import { useGroupDetailScreen } from './hooks';

export const GroupDetailScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { token } = useSelector((state) => state.auth);

    const {
        group,
        isLoading,
        isSavingGeneral,
        isSavingUsers,
        isDisabling,
        error,
        activeTab,
        tabs,
        generalForm,
        searchTerm,
        selectedUsers,
        filteredUsers,
        statusClassMap,
        formatDate,
        setActiveTab,
        setSearchTerm,
        handleGeneralInputChange,
        handleSaveGeneral,
        handleAddUser,
        handleRemoveUser,
        handleSaveUsers,
        handleDisableGroup,
    } = useGroupDetailScreen({
        token,
        id,
        navigate,
        location,
    });

    return (
        <>
            <div className="row">
                <div className="col-xxl-4 col-xl-6">
                    <div className="card stretch stretch-full">
                        <div className="card-body">
                            {isLoading && <div className="py-3 text-muted">Cargando grupo...</div>}

                            {!isLoading && error && (
                                <div className="alert alert-danger py-2" role="alert">{error}</div>
                            )}

                            {!isLoading && !error && group && (
                                <div className="mb-4 text-center">
                                    <div className="wd-150 ht-150 mx-auto mb-3 rounded-circle border border-5 border-gray-3 d-flex align-items-center justify-content-center">
                                        <i className="feather-users text-primary fs-1"></i>
                                    </div>
                                    <div className="mb-4">
                                        <span className="fs-14 fw-bold d-block">{group.name || '-'}</span>
                                        <span className="fs-12 fw-normal text-muted d-block">{group.code || '-'}</span>
                                    </div>
                                    <div className="fs-12 fw-normal text-muted text-center d-flex flex-wrap gap-3 mb-4">
                                        <div className="flex-fill py-3 px-4 rounded-1 d-none d-sm-block border border-dashed border-gray-5">
                                            <h6 className="fs-15 fw-bolder">{group.code || '-'}</h6>
                                            <p className="fs-12 text-muted mb-0">Código</p>
                                        </div>
                                        <div className="flex-fill py-3 px-4 rounded-1 d-none d-sm-block border border-dashed border-gray-5">
                                            <h6 className="fs-15 fw-bolder">
                                                <span className={`badge ${statusClassMap[group.status] || 'bg-soft-secondary text-secondary'}`}>{group.status || '-'}</span>
                                            </h6>
                                            <p className="fs-12 text-muted mb-0">Estatus</p>
                                        </div>
                                        <div className="flex-fill py-3 px-4 rounded-1 d-none d-sm-block border border-dashed border-gray-5">
                                            <h6 className="fs-15 fw-bolder">{Array.isArray(group.users) ? group.users.length : 0}</h6>
                                            <p className="fs-12 text-muted mb-0">Usuarios</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-xxl-8 col-xl-6 px-xl-3 px-xxl-4">
                    <div className="card border-top-0">
                        <div className="nav nav-tabs pt-0 pb-0">
                            <AppTabs
                                tabs={tabs}
                                activeTab={activeTab}
                                onChange={setActiveTab}
                                navClassName="nav nav-tabs flex-wrap w-100 customers-nav-tabs"
                                itemClassName="nav-item border-top border-end"
                            />
                        </div>

                        <div className="tab-content">
                            <AppTabPanel isActive={activeTab === 'general'} className="p-4">
                                {isLoading && <div className="py-3 text-muted">Cargando detalles...</div>}

                                {!isLoading && !error && group && (
                                    <div className="profile-details mb-1">
                                        <div className="mb-4 d-flex align-items-center justify-content-between">
                                            <h5 className="fw-bold mb-0">Detalles del grupo:</h5>
                                            <div className="d-flex gap-2">
                                                <button type="button" className="btn btn-primary btn-sm" onClick={handleSaveGeneral} disabled={isSavingGeneral}>
                                                    {isSavingGeneral ? 'Guardando...' : 'Guardar'}
                                                </button>
                                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleDisableGroup} disabled={isDisabling}>
                                                    {isDisabling ? 'Inhabilitando...' : 'Inhabilitar grupo'}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="row g-3 mb-3">
                                            <div className="col-md-8">
                                                <label className="form-label" htmlFor="group-name">Nombre</label>
                                                <input id="group-name" name="name" className="form-control" value={generalForm.name} onChange={handleGeneralInputChange} />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label" htmlFor="group-status">Estatus</label>
                                                <select id="group-status" name="status" className="form-select form-select-sm" value={generalForm.status} onChange={handleGeneralInputChange}>
                                                    <option value="ACTIVE">Activo</option>
                                                    <option value="INACTIVE">Inactivo</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="row g-0 mb-3">
                                            <div className="col-sm-6 text-muted">Creado:</div>
                                            <div className="col-sm-6 fw-semibold">{formatDate(group.createdDate)}</div>
                                        </div>
                                    </div>
                                )}
                            </AppTabPanel>

                            <AppTabPanel isActive={activeTab === 'users'} className="p-4">
                                {isLoading && <div className="py-3 text-muted">Cargando usuarios...</div>}

                                {!isLoading && !error && (
                                    <div>
                                        <div className="mb-4 d-flex align-items-center justify-content-between">
                                            <h5 className="fw-bold mb-0">Usuarios del grupo</h5>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={handleSaveUsers}
                                                disabled={isSavingUsers}
                                            >
                                                {isSavingUsers ? 'Guardando...' : 'Guardar usuarios'}
                                            </button>
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="group-user-search" className="form-label">Buscar usuario</label>
                                            <input
                                                id="group-user-search"
                                                type="text"
                                                className="form-control"
                                                placeholder="Escribe nombre, email o rol"
                                                value={searchTerm}
                                                onChange={(event) => setSearchTerm(event.target.value)}
                                            />

                                            {searchTerm.trim().length > 0 && (
                                                <div className="border border-gray-3 rounded-2 mt-2">
                                                    {filteredUsers.length === 0 && (
                                                        <div className="p-2 text-muted">Sin resultados para esta búsqueda.</div>
                                                    )}

                                                    {filteredUsers.length > 0 && (
                                                        <div className="list-group list-group-flush">
                                                            {filteredUsers.map((user) => (
                                                                <button
                                                                    key={user._id}
                                                                    type="button"
                                                                    className="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                                                                    onClick={() => handleAddUser(user._id)}
                                                                >
                                                                    <span>{user?.name || '-'} ({user?.email || '-'})</span>
                                                                    <span className="badge bg-soft-primary text-primary">Agregar</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {selectedUsers.length === 0 && (
                                            <div className="py-2 text-muted">No hay usuarios seleccionados en este grupo.</div>
                                        )}

                                        {selectedUsers.length > 0 && (
                                            <div className="table-responsive">
                                                <table className="table table-bordered mb-0 align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th>Nombre</th>
                                                            <th>Email</th>
                                                            <th>Rol</th>
                                                            <th style={{ width: '90px' }}>Acción</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {selectedUsers.map((user) => (
                                                            <tr key={user._id}>
                                                                <td>{user?.name || '-'}</td>
                                                                <td>{user?.email || '-'}</td>
                                                                <td>{user?.role || '-'}</td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() => handleRemoveUser(user._id)}
                                                                    >
                                                                        Quitar
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </AppTabPanel>
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                draggable
                theme="light"
            />
        </>
    );
};
